import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingDown,
  Cloud,
  DollarSign,
  Zap,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface CrossCloudMetrics {
  total_transfers: number;
  total_gb_transferred: number;
  total_cost: number;
  estimated_savings: number;
  provider_breakdown: Record<string, {
    transfers: number;
    gb_transferred: number;
    cost: number;
  }>;
  optimization_rate_percent: number;
  cache_hit_rate_percent?: number;
}

interface CostTrend {
  date: string;
  cost: number;
  savings: number;
  transfers: number;
}

interface CacheStats {
  hit_rate: number;
  miss_rate: number;
  total_requests: number;
  cache_size_gb: number;
}

interface CostAlert {
  id: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  amount: number;
  threshold: number;
  triggered_at: string;
}

const PROVIDER_COLORS = {
  'aws-to-gcp': '#FF6B6B',
  'gcp-to-aws': '#4ECDC4',
  'aws-to-azure': '#45B7D1',
  'azure-to-aws': '#96CEB4',
  'gcp-to-azure': '#FFEAA7',
  'azure-to-gcp': '#DDA0DD',
};

export default function CrossCloudCostMonitor() {
  const [metrics, setMetrics] = useState<CrossCloudMetrics | null>(null);
  const [costTrends, setCostTrends] = useState<CostTrend[]>([]);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [alerts, setAlerts] = useState<CostAlert[]>([]);
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [loadError, setLoadError] = useState<string | null>(null);

  const { toast } = useToast();

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      // Fetch system-wide cross-cloud billing summary (admin endpoint)
      const metricsRes = await fetchWithAuth(`/api/v1/storage/cross-cloud/billing/summary?days=${timeRange}`);

      if (!metricsRes.ok) throw new Error('Failed to fetch metrics');

      const metricsData = await metricsRes.json();
      const sanitizedMetrics: CrossCloudMetrics = {
        total_transfers: metricsData.total_transfers ?? 0,
        total_gb_transferred: metricsData.total_gb_transferred ?? 0,
        total_cost: metricsData.total_cost ?? 0,
        estimated_savings: metricsData.estimated_savings ?? 0,
        provider_breakdown: metricsData.provider_breakdown ?? {},
        optimization_rate_percent: metricsData.optimization_rate_percent ?? 0,
        cache_hit_rate_percent: metricsData.cache_hit_rate_percent ?? undefined,
      };
      setMetrics(sanitizedMetrics);

      // Generate cost trends from last 30 days
      // In production, this would come from a dedicated trends endpoint
      const trends: CostTrend[] = [];
      const days = parseInt(timeRange);
      for (let i = days; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        trends.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          cost: Math.random() * 50 + 10, // Mock data - replace with real API
          savings: Math.random() * 200 + 50,
          transfers: Math.floor(Math.random() * 1000 + 100),
        });
      }
      setCostTrends(trends);

      // Mock cache stats - replace with real API endpoint
      setCacheStats({
        hit_rate: sanitizedMetrics.cache_hit_rate_percent ?? 85.5,
        miss_rate: 14.5,
        total_requests: Math.floor(sanitizedMetrics.total_transfers * 1.2),
        cache_size_gb: sanitizedMetrics.total_gb_transferred * 0.3,
      });

      // Mock alerts - replace with real alerts endpoint
      const mockAlerts: CostAlert[] = [];
      if (sanitizedMetrics.total_cost > 1000) {
        mockAlerts.push({
          id: '1',
          severity: 'high',
          message: 'Monthly cross-cloud cost exceeds $1000 threshold',
          amount: sanitizedMetrics.total_cost,
          threshold: 1000,
          triggered_at: new Date().toISOString(),
        });
      }
      if (sanitizedMetrics.optimization_rate_percent < 70) {
        mockAlerts.push({
          id: '2',
          severity: 'medium',
          message: 'Optimization rate below 70% - review caching strategy',
          amount: sanitizedMetrics.optimization_rate_percent,
          threshold: 70,
          triggered_at: new Date().toISOString(),
        });
      }
      setAlerts(mockAlerts);

      setLastRefresh(new Date());
    } catch (error) {
      // The toast hook has no mounted renderer, so an inline banner is the
      // only feedback the admin actually sees — don't fail silently.
      setLoadError('Failed to load cross-cloud cost data');
      toast({
        title: 'Error loading metrics',
        description: 'Failed to load cross-cloud cost data',
        variant: 'destructive',
      });
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  const formatBytes = (gb: number) => {
    if (gb < 1) return `${(gb * 1000).toFixed(0)} MB`;
    if (gb < 1000) return `${gb.toFixed(1)} GB`;
    return `${(gb / 1000).toFixed(1)} TB`;
  };

  const totalCost = metrics?.total_cost ?? 0;
  const estimatedSavings = metrics?.estimated_savings ?? 0;
  const optimizationRate = metrics?.optimization_rate_percent ?? 0;
  const totalTransfers = metrics?.total_transfers ?? 0;
  const totalGbTransferred = metrics?.total_gb_transferred ?? 0;
  const cacheHitRate = cacheStats?.hit_rate ?? 0;
  const cacheTotalRequests = cacheStats?.total_requests ?? 0;
  const providerBreakdown = metrics?.provider_breakdown ?? {};
  const providerBreakdownEntries = Object.entries(providerBreakdown);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-white" />
        <span className="ml-2 text-white">Loading cost metrics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-white">Cross-Cloud Cost Monitor</h2>
          <p className="text-gray-400">
            System-wide data transfer costs and optimization across cloud providers
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-gray-800 text-white border border-gray-700 rounded-md px-3 py-2"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button onClick={fetchMetrics} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Load Error Banner */}
      {loadError && (
        <div className="p-4 rounded-md border border-red-800 bg-red-900/20" role="alert">
          <div className="flex items-start">
            <AlertTriangle className="h-4 w-4 mt-0.5 mr-2 text-red-400" />
            <div>
              <div className="font-medium text-red-400">Unable to load cross-cloud metrics</div>
              <div className="text-sm text-red-300 mt-1">{loadError}</div>
            </div>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert) => (
            <div key={alert.id} className={`p-4 rounded-md border ${alert.severity === 'high' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'}`}>
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 mt-0.5 mr-2" />
                <div>
                  <div className="font-medium">{alert.message}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {formatCurrency(alert.amount)} / {formatCurrency(alert.threshold)} threshold
                    <span className="ml-2">• {new Date(alert.triggered_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalCost)}
            </div>
            <p className="text-xs text-muted-foreground">
              {totalTransfers} transfers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Savings</CardTitle>
            <TrendingDown className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(estimatedSavings)}
            </div>
            <p className="text-xs text-muted-foreground">
              {optimizationRate.toFixed(1)}% optimization rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Transferred</CardTitle>
            <Cloud className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatBytes(totalGbTransferred)}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all providers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cache Performance</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {cacheHitRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {cacheTotalRequests} total requests
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Cost Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cost & Savings Trends</CardTitle>
            <CardDescription>
              Daily cross-cloud transfer costs and optimization savings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Cost"
                />
                <Line
                  type="monotone"
                  dataKey="savings"
                  stroke="#22c55e"
                  strokeWidth={2}
                  name="Savings"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Provider Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Breakdown</CardTitle>
            <CardDescription>
              Transfer costs by cloud provider routes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {providerBreakdownEntries.map(([provider, data]) => (
                <div key={provider} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: PROVIDER_COLORS[provider as keyof typeof PROVIDER_COLORS] || '#999' }}
                    />
                    <span className="text-sm font-medium">{provider.toUpperCase()}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{formatCurrency(data.cost)}</div>
                    <div className="text-xs text-muted-foreground">
                      {data.transfers} transfers, {formatBytes(data.gb_transferred)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground">
        Last updated: {lastRefresh.toLocaleTimeString()} •
        Auto-refresh every 5 minutes
      </div>
    </div>
  );
}
